import { useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import styled from "styled-components";
import { BottomSheet, FieldCheckBox, AlertModal } from "components/voc";
import { useModalState } from "hooks";

import Datepicker from "vanillajs-datepicker/Datepicker";
// @ts-ignore
import ko from "vanillajs-datepicker/locales/ko";
import "vanillajs-datepicker/css/datepicker-foundation.min.css";

import PagePreNone from "assets/images/element_page_pre_none.png";

type DatePickerSheetProps = {
  show: boolean;
  onClose: () => void;
  onEmit?: (data: any) => void;
};

const DatePickerSheetWrapper = styled.div`
  .cont_header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 2rem 0;
    padding-bottom: 1rem;
    border-bottom: 0.2rem solid #000;
    line-height: 2.2rem;
  }
  .cont_header:first-child {
    z-index: 1;
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    margin-top: 0;
    padding-top: 2rem;
    background-color: #fff;
  }
  .cont_header .txt_title {
    font-weight: 700;
  }
  .cont_header .txt_title em {
    font-weight: 400;
    color: #e10032;
    font-size: 1.4rem;
  }
  .cont_header .txt_data {
    color: #e10032;
    font-weight: 700;
  }
  .cont_header .txt_noti {
    color: #8f8f8f;
    font-size: 1.4rem;
  }
  .box_time {
    margin: 0 -0.2rem;
  }
  .box_time [class^="time_"] {
    position: relative;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-flow: row wrap;
    padding-left: 3.6rem;
  }

  .box_time .txt {
    position: absolute;
    top: 0.8rem;
    left: 0;
    font-weight: 700;
    font-size: 1.4rem;
    line-height: 2.2rem;
  }

  #calendar {
    font-family: "나눔 고딕", "droid sans fallback", "Apple SD Gothic Neo", sans-serif;
  }
  #calendar .datepicker {
    width: 100%;
  }
  #calendar .datepicker-controls {
    margin: 0 4rem;
  }
  #calendar .datepicker-controls .view-switch {
    font-family: inherit;
    font-weight: 700;
    font-size: 2rem;
    line-height: 2.4rem;
  }
  #calendar .datepicker-controls .prev-button,
  #calendar .datepicker-controls .next-button {
    width: 2.5rem;
    height: 2.5rem;
    background: url(${PagePreNone}) no-repeat center;
    background-size: 2.5rem;
    font-size: 0;
  }
  #calendar .datepicker-controls .next-button:disabled {
    visibility: hidden;
  }
  #calendar .datepicker-controls .next-button {
    transform: rotate(180deg);
  }
  #calendar .datepicker-view {
    width: 100%;
  }
  #calendar .days-of-week {
    margin: 0 1.5rem;
    border-bottom: 1px solid #dee3eb;
    color: #b2b7be;
  }
  #calendar .days-of-week .dow {
    width: 3rem;
    height: 3rem;
    font-weight: 400;
    font-size: 1.3rem;
  }
  #calendar .days-of-week .dow + .dow {
    margin-left: 0.8rem;
  }
  #calendar .datepicker-grid .datepicker-cell.focused:not(.selected) {
    background-color: #fff;
  }
  #calendar .datepicker-grid .datepicker-cell.selected,
  #calendar .datepicker-grid .datepicker-cell.selected:hover,
  #calendar .datepicker-grid .datepicker-cell.selected.today.focused {
    background-color: #f44a4d;
    color: #fff !important;
    font-weight: 700;
  }
  #calendar .datepicker-grid .day {
    width: 3.8rem;
    height: 3.8rem;
    font-size: 1.4rem;
    line-height: 1.6rem;
  }
  #calendar .datepicker-grid .day:not(.prev, .next, .disabled) {
    color: #222;
  }
  #calendar .datepicker-grid .day:nth-child(7n + 1):not(.prev, .next, .disabled) {
    color: #f44a4d;
  }
  #calendar .datepicker-grid .day.today,
  #calendar .datepicker-grid .day.today.focused {
    background-color: #f1f1f1;
  }
  #calendar .datepicker-grid .day.disabled {
    color: #a8b2ba;
  }
`;

const DatePickerSheet = ({ onEmit, ...props }: DatePickerSheetProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  let datepicker: Datepicker;

  const [dateState, setDateState] = useState("");
  const [times, setTimes] = useState<number[]>([]);
  const [prevAllocTimeState, setPrevAllocTimeState] = useState<any[] | undefined>([]);

  const modalState = useModalState();

  const initDatePicker = () => {
    Object.assign(Datepicker.locales, ko);

    const elem = calendarRef.current!;
    datepicker = new Datepicker(elem, {
      language: "ko",
      maxDate: new Date(),
    });

    datepicker.element.addEventListener("changeDate", (e) => {
      setDateState(datepicker.getDate("yyyy-mm-dd") as string);
    });

    datepicker.setDate(new Date());
  };

  const validationSchema = yup.object().shape({
    alloc_time: yup.array(),
  });

  const methods = useForm({
    ...(validationSchema && { resolver: yupResolver(validationSchema) }),
  });

  const onSubmit = (data: any) => {
    if (data.alloc_time.length === 0) {
      modalState.show();
      return;
    }
    const param = {
      alloc_date: dateState,
      // alloc_time: data.alloc_time.join(","),
      alloc_time: data.alloc_time,
    };

    if (onEmit) {
      onEmit(param);
    }

    props.onClose();
  };

  const handleAllocTime = () => {
    const allocTimeState = methods.getValues("alloc_time") || [];
    if (allocTimeState.length > 3) {
      methods.setValue("alloc_time", prevAllocTimeState);
    } else {
      setPrevAllocTimeState(methods.getValues("alloc_time"));
    }
  };

  useEffect(() => {
    initDatePicker();
  }, []);

  useEffect(() => {
    const now = new Date();
    if (new Date(dateState).toLocaleDateString() === now.toLocaleDateString()) {
      setTimes([...Array(now.getHours() + 1)].map((_, idx) => idx++));
    } else {
      setTimes([...Array(24)].map((_, idx) => idx++));
    }
  }, [dateState]);

  useEffect(() => {
    handleAllocTime();
  }, [methods.watch("alloc_time")]);

  const alertMsg = {
    title: (
      <>
        광고 참여 날짜, 시간대가
        <br />
        선택되지 않았습니다
        <br />
        <em className="txt_red">(모두 필수 선택 필요)</em>
      </>
    ),
  };

  const formId = "date-picker-form";

  return (
    <>
      <BottomSheet title="광고 참여 날짜 & 시간 등록" formId={formId} {...props}>
        <DatePickerSheetWrapper>
          <FormProvider {...methods}>
            <form id={formId} {...(onSubmit && { onSubmit: methods.handleSubmit(onSubmit) })}>
              <div className="cont_header">
                <strong className="txt_title">
                  광고 참여 날짜 <em>(필수)</em>
                </strong>
                <span className="txt_data">{dateState}</span>
              </div>
              <div className="box_calendar">
                <div id="calendar" ref={calendarRef}></div>
              </div>
              <div className="cont_header">
                <strong className="txt_title">
                  광고 참여 시간대 <em>(필수)</em>
                </strong>
                <span className="txt_noti">최대 3개 복수 선택 가능</span>
              </div>
              <div className="box_time">
                <div className="time_am">
                  <span className="txt">오전</span>
                  {times
                    .filter((x) => x < 12)
                    .map((el, _) => (
                      <FieldCheckBox type={"checkbox"} key={el} name="alloc_time" id={`alloc_time_${el}`} value={el} labelText={`${el}시`} />
                    ))}
                </div>
                {times.length > 12 && (
                  <div className="time_pm">
                    <span className="txt">오후</span>
                    {times
                      .filter((x) => x > 11)
                      .map((el, _) => (
                        <FieldCheckBox type={"checkbox"} key={el} name="alloc_time" id={`alloc_time_${el}`} value={el} labelText={`${el}시`} />
                      ))}
                  </div>
                )}
              </div>
            </form>
          </FormProvider>
        </DatePickerSheetWrapper>
        <AlertModal {...alertMsg} show={modalState.showState} onClose={modalState.show} />
      </BottomSheet>
    </>
  );
};

export default DatePickerSheet;
